import { useState, useEffect } from 'react';

interface NavbarProps {
  onOpenModal: () => void;
}

export default function Navbar({ onOpenModal }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (id: string) => {
    setIsMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
          isScrolled ? 'shadow-md' : ''
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src="https://static.readdy.ai/image/59087287535f4824e3cc6dc57c68b3ff/4753bc3a535e1240ff990232f4d40a80.png"
                alt="Laurie's Flash Framers"
                className="h-10 md:h-12 w-auto object-contain"
              />
              <div className="hidden md:block">
                <h1 className="text-lg md:text-xl font-serif font-bold text-black whitespace-nowrap">Laurie's Flash Framers</h1>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <button
                onClick={() => scrollToSection('home')}
                className="text-sm font-medium text-black hover:text-[#D4B137] transition-colors whitespace-nowrap cursor-pointer"
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-sm font-medium text-black hover:text-[#D4B137] transition-colors whitespace-nowrap cursor-pointer"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className="text-sm font-medium text-black hover:text-[#D4B137] transition-colors whitespace-nowrap cursor-pointer"
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection('portfolio')}
                className="text-sm font-medium text-black hover:text-[#D4B137] transition-colors whitespace-nowrap cursor-pointer"
              >
                Portfolio
              </button>
              <button
                onClick={() => scrollToSection('testimonials')}
                className="text-sm font-medium text-black hover:text-[#D4B137] transition-colors whitespace-nowrap cursor-pointer"
              >
                Testimonials
              </button>
              <button
                onClick={onOpenModal}
                className="px-6 py-2 bg-[#D4B137] text-black text-sm font-medium rounded-full hover:bg-[#D4B137]/90 transition-colors whitespace-nowrap cursor-pointer"
              >
                Contact Us
              </button>
            </div>

            {/* Mobile Hamburger Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
              aria-label="Toggle menu"
            >
              <div className="relative w-6 h-5">
                <span
                  className={`absolute left-0 w-6 h-0.5 bg-black transition-all duration-300 ${
                    isMobileMenuOpen ? 'top-2 rotate-45' : 'top-0'
                  }`}
                ></span>
                <span
                  className={`absolute left-0 top-2 w-6 h-0.5 bg-black transition-all duration-300 ${
                    isMobileMenuOpen ? 'opacity-0' : 'opacity-100'
                  }`}
                ></span>
                <span
                  className={`absolute left-0 w-6 h-0.5 bg-black transition-all duration-300 ${
                    isMobileMenuOpen ? 'top-2 -rotate-45' : 'top-4'
                  }`}
                ></span>
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      ></div>

      {/* Mobile Menu Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-72 bg-white z-50 md:hidden transform transition-transform duration-300 ease-out shadow-xl ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <button
          onClick={() => setIsMobileMenuOpen(false)}
          className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center text-gray-500 hover:text-black transition-colors cursor-pointer"
          aria-label="Close menu"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>
        <div className="pt-20 px-6">
          <div className="flex flex-col gap-2">
            <button
              onClick={() => scrollToSection('home')}
              className="text-left py-3 text-base font-medium text-black hover:text-[#D4B137] transition-colors cursor-pointer border-b border-gray-100"
            >
              Home
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-left py-3 text-base font-medium text-black hover:text-[#D4B137] transition-colors cursor-pointer border-b border-gray-100"
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="text-left py-3 text-base font-medium text-black hover:text-[#D4B137] transition-colors cursor-pointer border-b border-gray-100"
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection('portfolio')}
              className="text-left py-3 text-base font-medium text-black hover:text-[#D4B137] transition-colors cursor-pointer border-b border-gray-100"
            >
              Portfolio
            </button>
            <button
              onClick={() => scrollToSection('testimonials')}
              className="text-left py-3 text-base font-medium text-black hover:text-[#D4B137] transition-colors cursor-pointer border-b border-gray-100"
            >
              Testimonials
            </button>
            <button
              onClick={() => {
                setIsMobileMenuOpen(false);
                onOpenModal();
              }}
              className="mt-4 w-full py-3 bg-[#D4B137] text-black text-base font-medium rounded-full hover:bg-[#D4B137]/90 transition-colors cursor-pointer whitespace-nowrap"
            >
              Contact Us
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
